/**
 * useNotificacoesDoSino — dados do sino do header (C-24).
 *
 * Lê as MESMAS queries do Painel (`useM1Data`) — mesmas queryKeys, então o TanStack Query
 * compartilha o fetch — e monta a lista no domínio (`montarNotificacoesDoSino`). Regra #2:
 * nada aqui lê Supabase direto; tudo passa pelos datasources.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { pollingQueryOptions } from '@/lib/query-polling';
import { itensQuePedemAcao } from '@/domain/dashboard/atencao';
import { computeAtrasos } from '@/domain/notificacoes/atraso-de-leitura';
import {
  contarNaoLidas,
  montarNotificacoesDoSino,
  navegacaoDaAcao,
  type NotificacaoDoSino,
} from '@/domain/notificacoes/sino';
import { readAtalhoSemFluxo } from '@/domain/tarp/disparo';
import type { NotifProfile } from '@/domain/tarp/notificacoes';
import { useDataSources } from '@/services/datasources';
import { useNotificacoesStore } from '@/stores/notificacoesStore';
import { useAgoraQueAcompanha } from './useAgora';
import { useAtualizacaoAoVivo } from './useAtualizacaoAoVivo';
import { useDams } from './useDams';

export interface SinoDeNotificacoes {
  notificacoes: NotificacaoDoSino[];
  lidas: ReadonlySet<string>;
  naoLidas: number;
  isLoading: boolean;
  /** Falha de QUALQUER fonte: o sino não pode dizer "nada pendente" com a carga quebrada. */
  error: Error | null;
  /** Relógio da lista (anda com os dados e a cada 60 s) — o "há 12 min" sai dele. */
  agora: Date;
  abrir: (n: NotificacaoDoSino) => void;
  marcarTodasLidas: () => void;
}

export function useNotificacoesDoSino(profile: NotifProfile): SinoDeNotificacoes {
  const { instrumentacao, tarp, planos } = useDataSources();
  const navigate = useNavigate();
  const { lidas, marcarLida, marcarTodasLidas, podar } = useNotificacoesStore();
  // Mudança de TARP ou plano feita por outro usuário chega por evento, em qualquer tela;
  // o polling abaixo fica como rede de segurança.
  useAtualizacaoAoVivo();

  const { data: instruments = [], isLoading: l1, error: e1, dataUpdatedAt: t1 } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentacao.listInstruments(),
    ...pollingQueryOptions,
  });
  const { data: current = [], isLoading: l2, error: e2, dataUpdatedAt: t2 } = useQuery({
    queryKey: ['readings', 'current'],
    queryFn: () => instrumentacao.listCurrentReadings(),
    ...pollingQueryOptions,
  });
  const { data: dams = [], isLoading: l3, error: e3 } = useDams();
  // Todas as TARPs (não só as abertas): a supressão precisa das baixadas para a carência
  // pós-baixa (GUPM-221). Mesma queryKey do Painel.
  const { data: tarps = [], isLoading: l4, error: e4, dataUpdatedAt: t4 } = useQuery({
    queryKey: ['tarps', 'dashboard'],
    queryFn: () => tarp.listTarps(),
    ...pollingQueryOptions,
  });
  // Mesma queryKey da tela de Planos (`usePlanos`).
  const { data: listaPlanos = [], isLoading: l5, error: e5, dataUpdatedAt: t5 } = useQuery({
    queryKey: ['planos', 'list'],
    queryFn: () => planos.listPlanos(),
    ...pollingQueryOptions,
  });

  // Relógio que anda com os dados (e a cada 60 s): carga nova traz o "agora" do instante
  // em que chegou — o mesmo do Painel, que usa `new Date()` a cada render. Um `Date` novo
  // por render recalcularia a lista sempre. Os `= []` acima criam array novo a cada render
  // enquanto carrega, então o sinal é o `dataUpdatedAt` de cada query.
  const agora = useAgoraQueAcompanha([t1, t2, t4, t5]);

  const itens = useMemo(
    () => itensQuePedemAcao(instruments, current, dams, tarps, agora),
    [instruments, current, dams, tarps, agora],
  );
  // Leitura atrasada: mesma lista de instrumentos, julgada pelo `lastReadingAt` contra o
  // relógio — anda sozinha a cada 60 s, sem esperar carga nova (C-24, 20/10/2026).
  const atrasos = useMemo(() => computeAtrasos(instruments, dams, agora), [instruments, dams, agora]);
  const notificacoes = useMemo(
    () => montarNotificacoesDoSino({ itens, tarps, planos: listaPlanos, atrasos, profile, agora }),
    [itens, tarps, listaPlanos, atrasos, profile, agora],
  );

  // Esquece o "lido" do que saiu do sino: a pendência que volta conta de novo. A
  // assinatura em string evita podar a cada render (o array é novo a cada montagem).
  const assinatura = notificacoes.map((n) => n.chaveLeitura).join('\n');
  useEffect(() => {
    podar(assinatura === '' ? [] : assinatura.split('\n'));
  }, [assinatura, podar]);

  const abrir = (n: NotificacaoDoSino) => {
    marcarLida(n.chaveLeitura);
    const nav = navegacaoDaAcao(n.acao, instruments, itens);
    const semFluxo = readAtalhoSemFluxo(nav.state);
    // Mesmo registro do atalho do Painel (C-88): sem fluxo é caso a investigar.
    if (semFluxo) console.warn('[TARP] atalho do sino sem fluxo', { acao: n.acao, semFluxo });
    navigate(nav.path, { state: nav.state });
  };

  return {
    notificacoes,
    lidas,
    naoLidas: contarNaoLidas(notificacoes, lidas),
    isLoading: l1 || l2 || l3 || l4 || l5,
    error: (e1 ?? e2 ?? e3 ?? e4 ?? e5) as Error | null,
    agora,
    abrir,
    marcarTodasLidas: () => marcarTodasLidas(notificacoes.map((n) => n.chaveLeitura)),
  };
}
